import { useState, useEffect } from "react";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ClearedDialog = ({ sid, open, setOpen, onChange }) => {
  const [list, setList] = useState(null);
  const load = () => http.get(`/school-feed/${sid}/cleared`).then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { if (open) { setList(null); load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const restore = async (ids) => {
    try { const r = (await http.post(`/school-feed/${sid}/restore`, ids ? { ids } : {})).data; toast.success(`Brought back ${r.restored} item${r.restored === 1 ? "" : "s"}`); onChange(); ids ? load() : setOpen(false); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#FDFBF7] max-w-lg" data-testid="cleared-dialog">
        <DialogTitle className="font-display text-2xl text-[#2C4A3E]">Cleared school items</DialogTitle>
        <DialogDescription>Hidden by "Clear old school items". Bring back any you hid by mistake.</DialogDescription>
        <ul className="max-h-[50vh] overflow-y-auto divide-y divide-[#EEE8DA]" data-testid="cleared-list">
          {list === null && <li className="py-6 text-center text-sm text-stone-400">Loading…</li>}
          {list?.length === 0 && <li className="py-6 text-center text-sm text-stone-400">Nothing cleared.</li>}
          {list?.map((i) => (
            <li key={i.id} className="py-3 flex items-center gap-3" data-testid={`cleared-item-${i.id}`}>
              <span className="flex-1 min-w-0"><span className="block text-sm text-[#1F3D33] truncate">{i.topic}</span><span className="block text-xs text-stone-500">{i.subject_name} · {i.course} · due {i.date}</span></span>
              <button onClick={() => restore([i.id])} data-testid={`cleared-restore-${i.id}`} className="px-3 py-1.5 rounded-full border border-[#2C4A3E] text-[#2C4A3E] text-xs hover:bg-[#EEF3EF] transition-colors">Bring back</button>
            </li>))}
        </ul>
        {list?.length > 0 && <button onClick={() => restore(null)} data-testid="cleared-restore-all" className="w-full py-3 rounded-full bg-[#2C4A3E] text-white text-sm inline-flex items-center justify-center gap-2"><RotateCcw className="w-4 h-4" /> Restore all</button>}
      </DialogContent>
    </Dialog>
  );
};

export const ClearControls = ({ d, show, onChange }) => {
  const { profile } = useProfile();
  const [open, setOpen] = useState(false);
  if (!["student", "parent"].includes(profile?.role) || !show) return null;
  const sid = profile.role === "student" ? "me" : d.student_id;
  const clear = async () => {
    try {
      const r = (await http.post(`/school-feed/${sid}/clear-old`)).data;
      onChange();
      if (!r.cleared) return toast("Nothing to clear");
      toast.success(`Cleared ${r.cleared} old school item${r.cleared === 1 ? "" : "s"}`, {
        description: "They come back only if the due date changes.", duration: 8000,
        action: { label: "Undo", onClick: () => http.post(`/school-feed/${sid}/restore`, { ids: r.ids }).then(() => { toast.success("Brought them back"); onChange(); }).catch((e) => toast.error(formatApiError(e))) },
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl bg-[#FBF0DC] px-4 py-3 text-sm text-[#8A5A12]" data-testid="coursework-check-note">
      <span className="flex-1 min-w-[200px]">{d.items.length ? "These are past due, so the school may have locked them. Turned one in? Tap \"I turned it in\"." : "No past-due school items right now."}</span>
      {d.cleared_count > 0 && <button onClick={() => setOpen(true)} data-testid="coursework-show-cleared" className="text-xs underline text-[#2C4A3E]">Show cleared items ({d.cleared_count})</button>}
      {d.items.length > 0 && <button onClick={clear} data-testid="coursework-clear-old" className="px-4 py-2 rounded-full bg-white border border-[#E8C98F] text-[#2C4A3E] text-xs hover:bg-[#FFF8EA] transition-colors">Clear old school items</button>}
      <ClearedDialog sid={sid} open={open} setOpen={setOpen} onChange={onChange} />
    </div>
  );
};
